import React from 'react';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import { TrendType } from "components/atoms/Generic/TrendIndicator";
import PieChartIcon from 'assets/svg/PieChartIcon';
import CubeIcon from 'assets/svg/CubeIcon';
import StackedIcon from 'assets/svg/StackedIcon';
import FileSearchIcon from 'assets/svg/FileSearchIcon';
import type { WorkflowCardVariant } from 'components/molecules/Generic/WorkflowCard';
import { getFieldKey } from 'components/molecules/PPA/Modals/PPAField';
import { extractErrorMessage, formatNumWithKMBAlt } from './helperMethods';
import type { PPAFeatureConfig, PPAInputConfig } from 'types/ppa';
import { PpaScenarioActionType } from 'types/ppa';
import type {
    PPACreateViewRequest,
    PPAGroupByInput,
    PPAGetViewMetadataResponse,
    PPALensConfigInput,
    PPAScopeFilter,
    PPAScenarioColumnVisibilityInput,
} from 'types/ppaViews';
import { PPA_SCENARIO_MANAGER_ROUTE } from 'config/urls';

export interface KpiApiResponseItem {
    kpi_id: string;
    kpi_name: string;
    display_label: string;
    value: string;
    raw_value: number;
    growth_percentage: number | null;
    trend_direction: 'up' | 'down' | 'neutral';
    comparison_period: string;
}

export interface KpiDataMerged {
    display_label: string;
    value: string;
    raw_value: number | string;
    growth_percentage: number | null;
    trend_direction: 'up' | 'down' | 'neutral';
    valuePrefix?: string;
    valueSuffix?: string;
    show?: boolean;
    showTrend?: boolean;
    comparison_period: string;
    kpi_id: string;
    kpi_name: string;
    title?: string;
}


export interface KpiCardProps {
    title: string;
    value: string | number;
    valuePrefix?: string;
    valueSuffix?: string;
    showTrend?: boolean;
    trendValue?: string;
    trendType?: TrendType;
    style?: React.CSSProperties;
    className?: string;
    rawValue?: number | string;
}

/** Legacy config format (flat) */
export interface WorkflowCardConfigLegacy {
    workflow_id: string;
    title: string;
    description_points: string[];
    buttonLabel: string;
    iconVariant?: WorkflowCardVariant;
    icon?: string;
    iconColor?: string;
    isDisabled?: boolean;
    action_button: {
        label: string;
        iconVariant?: WorkflowCardVariant;
        icon?: string;
        iconColor?: string;
        endpoint?: string;
        isDisabled?: boolean;
    };
}

export interface WorkflowActionButtonConfig {
    label?: string;
    iconVariant?: WorkflowCardVariant;
    icon?: string;
    iconColor?: string;
    endpoint?: string;
    isDisabled?: boolean;
}

export interface WorkflowCardConfigItem {
    workflow_id?: string;
    title?: string;
    description_points?: string[];
    action_button?: WorkflowActionButtonConfig;
}

export type ExportKpiItem = {
    kpi_id?: string;
    display_label?: string;
    title?: string;
    value?: string | number;
    raw_value?: string | number | null;
    valuePrefix?: string;
    valueSuffix?: string;
    growth_percentage?: string | number | null;
    show?: boolean;
};

export type PerformanceSummaryExportOption = 'kpis' | 'overall_performance' | 'performance_by_lens';

export type ExportTableData = {
    headers: string[];
    rows: string[][];
};

const EMPTY_EXPORT_TABLE_DATA: ExportTableData = {
    headers: [],
    rows: [],
};

export const isPPAApp = () => {
    const isPPAApp = process.env.REACT_APP_IS_PPA_APP === 'true';
    return true;
};


export const ICONS: Record<string, React.ComponentType<{ color?: string }>> = {
    PieChartIcon, CubeIcon, StackedIcon, FileSearchIcon,
};

export function formatActivityDate(visitedAt: string | number | undefined): string | undefined {
    if (visitedAt == null || visitedAt === '') return undefined;
    try {
        const d = typeof visitedAt === 'number' ? dayjs.unix(visitedAt) : dayjs(visitedAt);
        return d.isValid() ? d.format('DD MMM YYYY') : undefined;
    } catch {
        return undefined;
    }
}

export function formatActivityTime(visitedAt: string | number | undefined): string | undefined {
    if (visitedAt == null || visitedAt === '') return undefined;
    try {
        const d = typeof visitedAt === 'number' ? dayjs.unix(visitedAt) : dayjs(visitedAt);
        return d.isValid() ? d.format('h:mm A') : undefined;
    } catch {
        return undefined;
    }
}


export const mergeKpiDataWithConfig = (
    data: KpiApiResponseItem[],
    config: any[] = []
): KpiDataMerged[] => {
    if (data.length > 0 && config.length > 0) {
        return data.map((item) => {
            const cfg = config.find((c) => c.title === item.display_label);
            return {
                display_label: cfg?.display_label ?? item.display_label,
                value: item.value,
                raw_value: item.raw_value,
                growth_percentage: item.growth_percentage,
                trend_direction: item.trend_direction,
                valuePrefix: cfg?.valuePrefix,
                valueSuffix: cfg?.valueSuffix,
                show: cfg?.show,
                showTrend: cfg?.showTrend ?? true,
                comparison_period: item.comparison_period,
                kpi_id: item.kpi_id,
                kpi_name: item.kpi_name,
            };
        });
    } else {
        return config.map((item) => {
            return {
                title: item.title,
                value: '-',
                valuePrefix: '',
                valueSuffix: '',
                show: item.show ?? true,
                showTrend: item.showTrend ?? true,
                display_label: item.title,
                raw_value: '-',
                growth_percentage: null,
                trend_direction: 'neutral',
                comparison_period: 'month',
                kpi_id: '',
                kpi_name: '',
            };
        });
    }
}

const normalizeWorkflowRoute = (endpoint?: string): string | undefined => {
    if (!endpoint) return undefined;
    return endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
};

type WorkflowCardSource = WorkflowCardConfigItem & {
    buttonLabel?: string;
    icon?: string;
    iconColor?: string;
    iconVariant?: WorkflowCardVariant;
    isDisabled?: boolean;
};

const hasWorkflowActionButtonFields = (actionButton?: WorkflowActionButtonConfig): boolean => {
    if (!actionButton) return false;
    return actionButton.label != null && actionButton.label !== '';
};

const buildWorkflowActionButton = (item: WorkflowCardSource): WorkflowActionButtonConfig | undefined => {
    const flatActionButton =
        item.buttonLabel != null && item.buttonLabel !== ''
            ? {
                label: item.buttonLabel,
                icon: item.icon,
                iconColor: item.iconColor,
                iconVariant: item.iconVariant,
                isDisabled: item.isDisabled,
            }
            : undefined;

    const merged: WorkflowActionButtonConfig = {
        ...flatActionButton,
        ...item.action_button,
    };

    return hasWorkflowActionButtonFields(merged) ? merged : undefined;
};

const isPlanAlignmentWorkflow = (item: WorkflowCardConfigItem): boolean => {
    const workflowId = item.workflow_id?.trim().toLowerCase();
    const title = item.title?.trim().toLowerCase();
    return workflowId === 'plan_alignment' || title === 'plan alignment';
};

export const resolveWorkflowNavigationEndpoint = (item: WorkflowCardConfigItem): string | undefined => {
    if (isPlanAlignmentWorkflow(item)) {
        return PPA_SCENARIO_MANAGER_ROUTE;
    }

    return normalizeWorkflowRoute(item.action_button?.endpoint);
};

const withResolvedWorkflowEndpoint = (item: WorkflowCardSource): WorkflowCardConfigItem => {
    const action_button = buildWorkflowActionButton(item);

    // Prevent endpoint-only action button creation
    if (!action_button || !action_button.label) {
        return {
            ...item,
            action_button: undefined,
        };
    }

    const endpoint = resolveWorkflowNavigationEndpoint(item);

    return {
        ...item,
        action_button: {
            ...action_button,
            endpoint: endpoint ?? action_button.endpoint,
        },
    };
};

export const mergeWorkflowDataWithConfig = (
    data: WorkflowCardConfigItem[] = [],
    config: WorkflowCardConfigItem[] = []
): WorkflowCardConfigItem[] => {
    // Use workflow id as the stable key; fall back to title when id is absent.
    const getKey = (item: WorkflowCardConfigItem) => item.workflow_id || item.title || '';
    const configMap = new Map(config.map((item) => [getKey(item), item]));

    if (!data.length) {
        return config.map(withResolvedWorkflowEndpoint);
    }

    const merged = data.map((dataItem) => {
        const configItem = configMap.get(getKey(dataItem));
        const mergedItem: WorkflowCardSource = {
            ...dataItem,
            ...configItem,
            description_points: configItem?.description_points ?? dataItem.description_points ?? [],
        };
        return withResolvedWorkflowEndpoint(mergedItem);
    });

    const mergedKeys = new Set(merged.map(getKey));
    const configOnlyItems = config
        .filter((item) => !mergedKeys.has(getKey(item)))
        .map(withResolvedWorkflowEndpoint);

    return [...merged, ...configOnlyItems];
};

const compactNumberFormatter = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
});

export const formatPpaMetricValue = (value?: string | number) => {
    if (value == null) return '-';
    if (typeof value === 'number') return compactNumberFormatter.format(value);

    const raw = String(value).trim();
    if (!raw) return '-';
    const match = raw.match(/^([^0-9+-]*)([+-]?\d[\d,]*(?:\.\d+)?)$/);
    if (!match) return raw;

    const [, prefix, numericPart] = match;
    const numeric = Number(numericPart.replace(/,/g, ''));
    return Number.isFinite(numeric) ? `${prefix}${compactNumberFormatter.format(numeric)}` : raw;
};

export const normalizePpaAttributeType = (value?: string) => (value ?? '').trim().toLowerCase();

/** Normalize API edit-row booleans for privacy (handles string/number from some backends). */
export function resolvePpaEditIsPrivate(editParams: Record<string, unknown> | undefined): boolean | undefined {
    if (!editParams || typeof editParams !== 'object') return undefined;
    const candidates = [
        editParams.is_private,
        editParams.project_visibility,
        editParams['isPrivate'],
    ];
    for (const v of candidates) {
        if (v === undefined || v === null) continue;
        if (typeof v === 'boolean') return v;
        if (typeof v === 'number') return v !== 0;
        if (typeof v === 'string') {
            const s = v.trim().toLowerCase();
            if (s === 'true' || s === '1') return true;
            if (s === 'false' || s === '0') return false;
        }
        return Boolean(v);
    }
    return undefined;
}

// ---------- Create-project form helpers ----------

/** Map API / table row data into config-driven form state keys used by PriceSimulatorCreateEditModal. */
export function mapEditParamsToPpaFormValues(
    featureConfig: PPAFeatureConfig,
    editParams: Record<string, any>,
): Record<string, any> {
    const out: Record<string, any> = {};
    if (!editParams || typeof editParams !== 'object') {
        return out;
    }

    const sim = editParams.simulation_time_period ?? {};
    const periodType =
        sim.simulation_period_type ?? editParams.simulation_period_type ?? 'custom_date';
    const startTs = sim.start_timestamp;
    const endTs = sim.end_timestamp;

    const filters: any[] = Array.isArray(editParams.project_filters) ? editParams.project_filters : [];
    const name = editParams.project_name ?? editParams.projectName;
    const resolvedPrivate = resolvePpaEditIsPrivate(editParams as Record<string, unknown>);

    featureConfig.options.inputs.forEach((inputConfig, index) => {
        const key = getFieldKey(inputConfig, index);

        if (inputConfig.input_id === 'project_name' && name != null) {
            out[key] = name;
            return;
        }

        if (inputConfig.input_id === 'scenario_name' && editParams.scenario_name != null) {
            out[key] = editParams.scenario_name;
            return;
        }

        if (
            (inputConfig.input_id === 'is_private' || inputConfig.input_id === 'project_visibility') &&
            resolvedPrivate !== undefined
        ) {
            out[key] = resolvedPrivate;
            return;
        }

        if (inputConfig.input_id === 'project_filters') {
            const match = filters.find(
                (f) =>
                    normalizePpaAttributeType(f.attribute_type) === normalizePpaAttributeType(inputConfig.attribute_type) &&
                    f.dimension === inputConfig.dimension,
            );
            const exIds = match?.attribute_ex_id;
            if (!Array.isArray(exIds) || exIds.length === 0) return;
            out[key] =
                inputConfig.input_type === 'multiselect' ? exIds : exIds[0] ?? exIds;
            return;
        }

        if (inputConfig.input_id === 'simulation_time_period' && startTs != null && endTs != null) {
            const selectionPayload = JSON.stringify({
                start_timestamp: startTs,
                end_timestamp: endTs,
            });
            const toDayjs = (ts: number) => (ts > 1e12 ? dayjs(ts) : dayjs.unix(ts));
            if (periodType === 'custom_date') {
                out[key] = {
                    period: 'custom_date',
                    range: [toDayjs(startTs).toDate(), toDayjs(endTs).toDate()],
                    selection: selectionPayload,
                };
            } else {
                out[key] = {
                    period: periodType,
                    range: [toDayjs(startTs), toDayjs(endTs)],
                    selection: selectionPayload,
                };
            }
            return;
        }

        // Any other configured input (e.g. data_source) maps 1:1 from edit row by input_id.
        const dynamicVal = editParams[inputConfig.input_id];
        if (dynamicVal !== undefined && dynamicVal !== null) {
            out[key] = dynamicVal;
        }
    });

    return out;
}

/** Always returns an array – used to count selections in a filter field. */
export function getSelectedValues(value: any): any[] {
    if (Array.isArray(value)) return value;
    if (value !== undefined && value !== null) return [value];
    return [];
}

function getPpaPrivacyToggleFormValue(
    featureConfig: PPAFeatureConfig,
    formValues: Record<string, any>,
): unknown {
    return (
        getFormValueByInputId(featureConfig, formValues, 'is_private') ??
        getFormValueByInputId(featureConfig, formValues, 'project_visibility')
    );
}

/** Looks up the current form value for a given input_id. */
export function getFormValueByInputId(
    featureConfig: PPAFeatureConfig,
    formValues: Record<string, any>,
    inputId: string,
): any {
    const inputIndex = featureConfig.options.inputs.findIndex(
        (input) => input.input_id === inputId,
    );
    const inputConfig = featureConfig.options.inputs[inputIndex];
    if (!inputConfig) return undefined;
    return formValues[getFieldKey(inputConfig, inputIndex)];
}

/** Returns all project_filters fields with their index and field key (order matters for cascade). */
export function getProjectFilterFields(
    featureConfig: PPAFeatureConfig,
): { inputConfig: PPAInputConfig; index: number; fieldKey: string }[] {
    return featureConfig.options.inputs.reduce(
        (filters, inputConfig, index) => {
            if (inputConfig.input_id !== 'project_filters') return filters;
            filters.push({ inputConfig, index, fieldKey: getFieldKey(inputConfig, index) });
            return filters;
        },
        [] as { inputConfig: PPAInputConfig; index: number; fieldKey: string }[],
    );
}

/** Returns the reset value when a parent filter changes: [] for multiselect, undefined otherwise. */
export function getClearedValue(inputConfig: PPAInputConfig): [] | undefined {
    return inputConfig.input_type === 'multiselect' ? [] : undefined;
}

/** User-facing copy aligned with Scenario Manager PRD (US-01). */
// NB: project-name duplicate copy lives server-side now (BE returns the
// message via the 409 `detail`) — the FE no longer pre-validates project
// uniqueness, see `getPpaCreateProjectFormValidationIssues`.
export const PPA_DUPLICATE_SCENARIO_NAME_MSG =
    'This scenario name already exists in this project. Please choose a different name.';
export const PPA_PROJECT_NAME_MIN_LENGTH_MSG = 'Project name must be more than 3 characters.';
export const PPA_SCENARIO_NAME_MIN_LENGTH_MSG = 'Scenario name must be more than 3 characters.';

const PPA_NAME_MIN_LENGTH = 3;

function appendPpaNameMinLengthIssue(
    issues: PpaCreateProjectFieldIssue[],
    nameFieldKey: string,
    name: string,
    message: string,
): void {
    if (nameFieldKey && name.length > 0 && name.length <= PPA_NAME_MIN_LENGTH) {
        issues.push({ fieldKey: nameFieldKey, message });
    }
}

export function getDuplicateScenarioNameCopy(originalName: string): string {
    const trimmed = String(originalName ?? '').trim();
    // When the original name is empty or only whitespace, return a meaningful fallback
    // instead of a terse '_copy' which would technically pass min-length checks but
    // is semantically invalid when sent to the API. Use 'Scenario_copy' as the
    // fallback to make intent clearer and to align with user-facing naming.
    return trimmed ? `${trimmed}_copy` : 'Scenario_copy';
}

const SCENARIO_NAME_SEQUENCE_PATTERN = /^Scenario_(\d+)$/i;

/** Next sequential name (Scenario_1, Scenario_2, …) per US-02; does not reuse deleted numbers. */
export function suggestNextScenarioName(existingNames: string[]): string {
    let maxNum = 0;
    for (const raw of existingNames) {
        const match = SCENARIO_NAME_SEQUENCE_PATTERN.exec(String(raw ?? '').trim());
        if (match) {
            maxNum = Math.max(maxNum, Number.parseInt(match[1], 10));
        }
    }
    return `Scenario_${maxNum + 1}`;
}
// Custom simulation periods are capped at 52 weeks. The cap is enforced
// visually by `DateRangePicker` (via `maxRangeWeeks`) so the user can't
// even pick an over-range window — no post-hoc validation needed.
// See commit e9b3ef8b ("enforce 52-week simulation limit in calendar
// instead of validation") for the rationale.
export const PPA_MAX_SIMULATION_WEEKS = 52;

export const PPA_SIMULATION_END_BEFORE_START_MSG = 'End date must be after the start date.';

const DEFAULT_PRESET_PERIOD_IDS = ['current_year', 'financial_year'];

export type PpaPeriodTimestamp = {
    period_id: string;
    start_timestamp: number;
    end_timestamp: number;
};

/** Normalizes `get_period_start_end_timestamps` response (array or wrapped). */
export function parsePeriodTimestampsResponse(responseData: unknown): PpaPeriodTimestamp[] {
    const raw = Array.isArray(responseData)
        ? responseData
        : Array.isArray((responseData as { data?: unknown })?.data)
          ? (responseData as { data: unknown[] }).data
          : Array.isArray((responseData as { periods?: unknown })?.periods)
            ? (responseData as { periods: unknown[] }).periods
            : [];

    return raw
        .map((item: Record<string, unknown>) => ({
            period_id: String(item?.period_id ?? ''),
            start_timestamp: Number(item?.start_timestamp),
            end_timestamp: Number(item?.end_timestamp),
        }))
        .filter(
            (item) =>
                item.period_id.length > 0 &&
                Number.isFinite(item.start_timestamp) &&
                Number.isFinite(item.end_timestamp),
        );
}

/** Preset period ids from scenario_manager meta (`simulation_time_period` drop_down, excluding custom_date). */
export function getSimulationPeriodPresetIdsFromMeta(meta?: {
    features?: Array<{
        feature_id?: string;
        options?: {
            inputs?: Array<{
                input_id?: string;
                drop_down?: Array<{ id?: string }> | null;
            }>;
        };
    }>;
}): string[] {
    const createProject = meta?.features?.find((f) => f.feature_id === 'create_project');
    const simInput = createProject?.options?.inputs?.find((i) => i.input_id === 'simulation_time_period');
    const ids =
        simInput?.drop_down
            ?.map((d) => d.id)
            .filter((id): id is string => Boolean(id) && id !== 'custom_date') ?? [];
    return ids.length > 0 ? ids : DEFAULT_PRESET_PERIOD_IDS;
}

/** Merges API timestamps into create_project → simulation_time_period drop_down on scenario_manager meta. */
export function mergePeriodTimestampsIntoScenarioManagerMeta<
    T extends {
        features?: Array<{
            feature_id?: string;
            feature_type?: string;
            options?: {
                inputs?: Array<{
                    input_id?: string;
                    drop_down?: Array<{
                        id?: string;
                        label?: string;
                        start_timestamp?: number;
                        end_timestamp?: number;
                    }> | null;
                }>;
            };
        }>;
    },
>(meta: T | undefined, periodTimestamps: PpaPeriodTimestamp[]): T | undefined {
    if (!meta || !periodTimestamps.length) return meta;

    const tsMap = new Map(periodTimestamps.map((pt) => [pt.period_id, pt]));

    return {
        ...meta,
        features: meta.features?.map((feature) => {
            if (feature.feature_id !== 'create_project' || feature.feature_type !== 'form') {
                return feature;
            }
            return {
                ...feature,
                options: {
                    ...feature.options,
                    inputs: feature.options?.inputs?.map((input) => {
                        if (input.input_id !== 'simulation_time_period' || !input.drop_down?.length) {
                            return input;
                        }
                        return {
                            ...input,
                            drop_down: input.drop_down.map((item) => {
                                const ts = tsMap.get(String(item.id ?? ''));
                                if (!ts) return item;
                                return {
                                    ...item,
                                    start_timestamp: ts.start_timestamp,
                                    end_timestamp: ts.end_timestamp,
                                };
                            }),
                        };
                    }),
                },
            };
        }),
    };
}

/**
 * Resolve simulation start/end unix seconds and period type from create-project form state.
 * Shared by `buildCreateProjectPayload` and client-side validation.
 */
export function resolvePpaSimulationTimestamps(
    featureConfig: PPAFeatureConfig,
    formValues: Record<string, any>,
): {
    period?: string;
    startTimestamp?: number;
    endTimestamp?: number;
} {
    const simPeriodIndex = featureConfig.options.inputs.findIndex(
        (input) => input.input_id === 'simulation_time_period',
    );
    const simPeriodInput = featureConfig.options.inputs[simPeriodIndex];
    const simPeriodValue = simPeriodInput
        ? formValues[getFieldKey(simPeriodInput, simPeriodIndex)]
        : undefined;

    const selectedPresetPeriod = simPeriodInput?.drop_down?.find((item) => item.id === simPeriodValue?.period);
    let parsedSelection: { start_timestamp?: number; end_timestamp?: number } = {};
    try {
        parsedSelection = simPeriodValue?.selection ? JSON.parse(simPeriodValue.selection) : {};
    } catch {
        parsedSelection = {};
    }
    const customDateRange = parsedSelection;

    const isCustomDate = simPeriodValue?.period === 'custom_date';
    const startTimestamp = isCustomDate
        ? customDateRange?.start_timestamp
        : parsedSelection?.start_timestamp ?? selectedPresetPeriod?.start_timestamp;
    const endTimestamp = isCustomDate
        ? customDateRange?.end_timestamp
        : parsedSelection?.end_timestamp ?? selectedPresetPeriod?.end_timestamp;

    return {
        period: simPeriodValue?.period,
        startTimestamp,
        endTimestamp,
    };
}

export type PpaCreateProjectFieldIssue = { fieldKey: string; message: string };

/**
 * Client-side checks: invalid range, custom period > 52 weeks.
 *
 * Duplicate-project-name detection used to live here but was removed when the
 * project list endpoint became paginated — a client walking only the current
 * page would surface false negatives against names sitting on other pages.
 * The backend's `_validate_project_name_unique` pre-flight check is now the
 * authoritative duplicate-name source; the 409 response is rendered through
 * `resolvePpaCreateProjectApiErrorMessage` as a submit-time toast.
 *
 * Required-field gaps are handled separately via `isInputValueMissing`.
 */
export function getPpaCreateProjectFormValidationIssues(
    featureConfig: PPAFeatureConfig,
    formValues: Record<string, any>,
): PpaCreateProjectFieldIssue[] {
    const issues: PpaCreateProjectFieldIssue[] = [];

    const nameFieldIndex = featureConfig.options.inputs.findIndex((i) => i.input_id === 'project_name');
    const simFieldIndex = featureConfig.options.inputs.findIndex((i) => i.input_id === 'simulation_time_period');

    const nameFieldKey =
        nameFieldIndex >= 0 ? getFieldKey(featureConfig.options.inputs[nameFieldIndex], nameFieldIndex) : '';
    const simFieldKey =
        simFieldIndex >= 0 ? getFieldKey(featureConfig.options.inputs[simFieldIndex], simFieldIndex) : '';

    const rawName = nameFieldKey ? formValues[nameFieldKey] : '';
    const name = String(rawName ?? '').trim();
    appendPpaNameMinLengthIssue(issues, nameFieldKey, name, PPA_PROJECT_NAME_MIN_LENGTH_MSG);

    if (simFieldKey && simFieldIndex >= 0) {
        const simInput = featureConfig.options.inputs[simFieldIndex];
        if (!isInputValueMissing(simInput, formValues, simFieldIndex)) {
            const { period, startTimestamp, endTimestamp } = resolvePpaSimulationTimestamps(
                featureConfig,
                formValues,
            );
            if (
                typeof startTimestamp === 'number' &&
                typeof endTimestamp === 'number' &&
                Number.isFinite(startTimestamp) &&
                Number.isFinite(endTimestamp)
            ) {
                if (endTimestamp <= startTimestamp) {
                    issues.push({ fieldKey: simFieldKey, message: PPA_SIMULATION_END_BEFORE_START_MSG });
                }
            }
        }
    }

    return issues;
}

export function getPpaCreateScenarioFormValidationIssues(
    featureConfig: PPAFeatureConfig,
    formValues: Record<string, any>,
    options: {
        existingScenarios: Array<{ scenario_id?: number | string; scenario_name: string }>;
        editingScenarioId?: number | string | null;
        isEdit: boolean;
        /** When true, `scenario_name` is optional (backend auto-generates if omitted). */
        isCreate?: boolean;
    },
): PpaCreateProjectFieldIssue[] {
    const issues: PpaCreateProjectFieldIssue[] = [];
    const nameFieldIndex = featureConfig.options.inputs.findIndex((i) => i.input_id === 'scenario_name');
    const nameFieldKey =
        nameFieldIndex >= 0 ? getFieldKey(featureConfig.options.inputs[nameFieldIndex], nameFieldIndex) : '';
    const rawName = nameFieldKey ? formValues[nameFieldKey] : '';
    const name = String(rawName ?? '').trim();

    if (!options.isCreate && nameFieldKey && !name) {
        issues.push({ fieldKey: nameFieldKey, message: 'Scenario name is required.' });
    }

    appendPpaNameMinLengthIssue(issues, nameFieldKey, name, PPA_SCENARIO_NAME_MIN_LENGTH_MSG);

    if (name && nameFieldKey && name.length > PPA_NAME_MIN_LENGTH) {
        const lower = name.toLowerCase();
        const clash = (options.existingScenarios ?? []).some((scenario) => {
            const scenarioName = String(scenario.scenario_name ?? '').trim().toLowerCase();
            if (scenarioName !== lower) return false;
            if (options.isEdit && options.editingScenarioId != null && options.editingScenarioId !== '') {
                return String(scenario.scenario_id) !== String(options.editingScenarioId);
            }
            return true;
        });
        if (clash) {
            issues.push({ fieldKey: nameFieldKey, message: PPA_DUPLICATE_SCENARIO_NAME_MSG });
        }
    }

    return issues;
}

/** Normalize create/update project API errors (string detail, FastAPI array, nested message). */
export function resolvePpaCreateProjectApiErrorMessage(error: any, fallback: string): string {
    const fromHelper = extractErrorMessage(error);
    if (fromHelper) return fromHelper;

    const detail = error?.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) {
        return detail.trim();
    }
    if (Array.isArray(detail) && detail.length > 0) {
        const joined = detail
            .map((item: unknown) => {
                if (typeof item === 'string') return item;
                if (item && typeof item === 'object') {
                    const o = item as Record<string, unknown>;
                    if (typeof o.msg === 'string') return o.msg;
                    if (typeof o.message === 'string') return o.message;
                    if (typeof o.description === 'string') return o.description;
                }
                return '';
            })
            .filter(Boolean)
            .join(', ');
        if (joined) return joined;
    }
    if (detail && typeof detail === 'object') {
        const o = detail as Record<string, unknown>;
        if (typeof o.msg === 'string' && o.msg.trim()) return o.msg.trim();
        if (typeof o.message === 'string' && o.message.trim()) return o.message.trim();
    }
    const topMessage = error?.response?.data?.message;
    if (typeof topMessage === 'string' && topMessage.trim()) {
        return topMessage.trim();
    }
    return fallback;
}

/** Returns true when a required field has no value. boolean type is never missing (false is valid). */
export function isInputValueMissing(
    inputConfig: PPAInputConfig,
    formValues: Record<string, any>,
    index: number,
): boolean {
    const value = formValues[getFieldKey(inputConfig, index)];

    switch (inputConfig.input_type) {
        case 'string':
            return !String(value ?? '').trim();
        case 'date_range':
            return !value?.period || !value?.selection;
        case 'singleselect':
            return value === undefined || value === null || value === '';
        case 'multiselect':
            return !Array.isArray(value) || value.length === 0;
        case 'boolean':
            return false;
        default:
            return false;
    }
}

/** Structural create-project inputs; any other configured `input_id` is copied to the payload under that key. */
const PPA_CREATE_PROJECT_STRUCTURAL_INPUT_IDS = new Set([
    'project_name',
    'simulation_time_period',
    'project_filters',
    'is_private',
    'project_visibility',
]);

/**
 * Assembles the API payload for create or update project.
 * Preset periods use timestamps from the dropdown option, or from stored `selection` JSON when editing.
 * Custom Date timestamps come from the date-range picker stored as a JSON string.
 * Additional metadata inputs (e.g. `data_source`) are included dynamically using each field's `input_id` as the API key.
 */
export function buildCreateProjectPayload(
    featureConfig: PPAFeatureConfig,
    formValues: Record<string, any>,
    options?: { projectId?: string | number | null },
) {
    const projectFilters = featureConfig.options.inputs.reduce((filters: any[], inputConfig, index) => {
        if (inputConfig.input_id !== 'project_filters') return filters;
        filters.push({
            dimension: inputConfig.dimension,
            attribute_type: inputConfig.attribute_type,
            attribute_ex_id: getSelectedValues(formValues[getFieldKey(inputConfig, index)]),
        });
        return filters;
    }, []);

    const { period: simPeriodType, startTimestamp, endTimestamp } = resolvePpaSimulationTimestamps(
        featureConfig,
        formValues,
    );

    const privacyToggleRaw = getPpaPrivacyToggleFormValue(featureConfig, formValues);

    const projectId = options?.projectId;

    const payload: Record<string, any> = {
        project_id: projectId === '' || projectId == null ? null : String(projectId),
        project_name: getFormValueByInputId(featureConfig, formValues, 'project_name'),
        created_by: localStorage.getItem('email'),
        simulation_time_period: {
            start_timestamp: startTimestamp,
            end_timestamp: endTimestamp,
            simulation_period_type: simPeriodType,
        },
        project_filters: projectFilters,
        is_private: Boolean(privacyToggleRaw),
    };

    featureConfig.options.inputs.forEach((inputConfig, index) => {
        if (PPA_CREATE_PROJECT_STRUCTURAL_INPUT_IDS.has(inputConfig.input_id)) return;
        const v = formValues[getFieldKey(inputConfig, index)];
        if (v !== undefined) {
            payload[inputConfig.input_id] = v;
        }
    });

    return payload;
}

function toPpaScenarioId(value: number | string | null | undefined): number | string | undefined {
    if (value === undefined || value === null || value === '') return undefined;
    const asNumber = Number(value);
    return Number.isFinite(asNumber) ? asNumber : String(value);
}

/**
 * POST /ppa/simulation/create_scenario
 * - Create: `project_id`, `created_by`, optional `scenario_name` (no `scenario_id`)
 * - Duplicate: same as create — no `scenario_id`
 * - Rename: `scenario_id`, `project_id`, `scenario_name`, `created_by`
 */
/** Direct duplicate (no modal) — create-style payload with `{name}_copy`. */
export function buildDuplicateScenarioPayload(
    projectId: number | string,
    sourceScenarioName: string,
): Record<string, unknown> {
    const createdBy = localStorage.getItem('email');
    const payload: Record<string, unknown> = {
        project_id: toPpaScenarioId(projectId) ?? projectId,
        scenario_name: getDuplicateScenarioNameCopy(sourceScenarioName),
    };
    if (createdBy) {
        payload.created_by = createdBy;
    }
    return payload;
}

export function buildCreateScenarioPayload(
    featureConfig: PPAFeatureConfig,
    formValues: Record<string, any>,
    options: {
        projectId: number | string;
        scenarioActionType: PpaScenarioActionType;
        scenarioId?: string | number | null;
    },
) {
    const scenarioName = String(
        getFormValueByInputId(featureConfig, formValues, 'scenario_name') ?? '',
    ).trim();
    const createdBy = localStorage.getItem('email');

    const payload: Record<string, unknown> = {
        project_id: toPpaScenarioId(options.projectId) ?? options.projectId,
    };

    if (createdBy) {
        payload.created_by = createdBy;
    }

    if (options.scenarioActionType === PpaScenarioActionType.Create) {
        if (scenarioName) {
            payload.scenario_name = scenarioName;
        }
        return payload;
    }

    const scenarioId = toPpaScenarioId(options.scenarioId);
    if (scenarioId !== undefined) {
        payload.scenario_id = scenarioId;
    }
    payload.scenario_name = scenarioName;

    return payload;
}

type PpaKpiLabelItem = {
    id: string;
    label: string;
};

type PpaKpiTreeNode<TKpi> = {
    dimension?: string;
    attribute_type?: string;
    ex_id?: string;
    description?: string;
    is_total?: boolean;
    kpis?: TKpi[];
    data?: PpaKpiTreeNode<TKpi>[];
};

const normalizePpaKpiNode = <TKpi,>(
    node: PpaKpiTreeNode<TKpi>,
    normalizeKpi: (kpi: TKpi) => TKpi
): PpaKpiTreeNode<TKpi> => ({
    dimension: node.dimension,
    attribute_type: node.attribute_type,
    ex_id: node.ex_id,
    description: node.description ?? node.ex_id ?? '-',
    is_total: Boolean(node.is_total),
    kpis: (node.kpis ?? []).map((kpi) => normalizeKpi(kpi)),
    data: (node.data ?? []).map((child) => normalizePpaKpiNode(child, normalizeKpi)),
});

export const normalizePpaKpiRows = <TKpi,>(
    rows: unknown[],
    normalizeKpi: (kpi: TKpi) => TKpi
): PpaKpiTreeNode<TKpi>[] =>
    rows
        .filter((row): row is PpaKpiTreeNode<TKpi> => typeof row === 'object' && row !== null)
        .map((row) => normalizePpaKpiNode(row, normalizeKpi));

const toPpaKpiLabelKey = (kpi: Pick<PpaKpiLabelItem, 'id' | 'label'>) => (kpi.label || kpi.id || '').trim().toLowerCase();

export const uniquePpaKpisByLabel = <TKpi extends PpaKpiLabelItem>(kpis: TKpi[]) => {
    const seen = new Set<string>();
    return kpis.filter((kpi) => {
        const key = toPpaKpiLabelKey(kpi);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

export const normalizePpaKpiIdsByLabel = <TKpi extends PpaKpiLabelItem>(ids: string[], allKpis: TKpi[]) => {
    const labelById = new Map(allKpis.map((kpi) => [kpi.id, toPpaKpiLabelKey(kpi)]));
    const canonicalByLabel = new Map<string, string>();
    allKpis.forEach((kpi) => {
        const key = labelById.get(kpi.id);
        if (key && !canonicalByLabel.has(key)) canonicalByLabel.set(key, kpi.id);
    });

    const seen = new Set<string>();
    return ids.reduce<string[]>((acc, id) => {
        const key = labelById.get(id) ?? id.toLowerCase();
        const canonical = canonicalByLabel.get(key) ?? id;
        if (!seen.has(canonical)) {
            seen.add(canonical);
            acc.push(canonical);
        }
        return acc;
    }, []);
};

/** One row from meta `granularity_column_mapping` on a Performance Summary tab. */
export type PpaGranularityMappingRow = {
    granularity_id: string;
    dim1_attribute_type: string;
    dim2_attribute_type: string;
    priority_order: number;
};

type PpaGranularityDimensionOption = {
    id: string;
    dimension?: string;
};

type PpaGranularityKpiOption = {
    granularity_id_list?: string[] | null;
};

/**
 * Resolves active granularity id (e.g. `v1` vs `v2`) for a tab based on its
 * `granularity_column_mapping` and the currently selected dimension options.
 *
 * - Returns `null` when mapping is empty/absent; caller should skip granularity filtering.
 * - `*` matches anything, including absence of a selected option for that slot.
 * - Rules are evaluated in ascending `priority_order`; first match wins.
 */
export const resolvePpaGranularityId = (
    mapping: PpaGranularityMappingRow[] | undefined,
    selectedDimensionOptions: PpaGranularityDimensionOption[]
): string | null => {
    if (!mapping?.length) return null;

    const dim1Type = normalizePpaAttributeType(selectedDimensionOptions.find((option) => option.dimension === 'dim1')?.id);
    const dim2Type = normalizePpaAttributeType(selectedDimensionOptions.find((option) => option.dimension === 'dim2')?.id);

    const slotMatches = (pattern: string, selected: string) =>
        pattern === '*' ? true : Boolean(selected) && normalizePpaAttributeType(pattern) === selected;

    const found = [...mapping]
        .sort((left, right) => left.priority_order - right.priority_order)
        .find((row) => slotMatches(row.dim1_attribute_type, dim1Type) && slotMatches(row.dim2_attribute_type, dim2Type));

    return found?.granularity_id ?? null;
};

/**
 * Filters KPI options by a resolved granularity id.
 * - `granularityId == null` → no filter (returns original list).
 * - Empty/absent `granularity_id_list` on an option = valid for all granularities.
 */
export const filterPpaKpisByGranularity = <T extends PpaGranularityKpiOption>(
    kpiOptions: T[],
    granularityId: string | null
): T[] => {
    if (granularityId == null) return kpiOptions;
    return kpiOptions.filter((option) => {
        const list = option.granularity_id_list;
        return !list?.length || list.includes(granularityId);
    });
};

export const downloadExcel = async (fileName: string, rows: Array<Array<string | number>>) => {
    const XLSX = await import('xlsx');
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Performance Summary');
    const workbookBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([workbookBuffer], { type: 'application/octet-stream' });
    saveAs(blob, `${fileName}.xlsx`);
};

export const formatKpiExportValue = (kpi: ExportKpiItem) => {
    const hasRawValue = kpi.raw_value !== undefined && kpi.raw_value !== null && kpi.raw_value !== '';
    const parsedRawValue = parseFloat(String(kpi.raw_value));
    const formattedBaseValue =
        hasRawValue && !Number.isNaN(parsedRawValue) ? parsedRawValue : kpi.value;
    const suffix = typeof kpi.valueSuffix === 'string' && kpi.valueSuffix.includes('%') ? kpi.valueSuffix : '';
    const exportValue = [kpi.valuePrefix, formattedBaseValue, suffix].filter(Boolean).join('');
    return exportValue || '-';
};

export const getPerformanceSummarySectionTableData = ({
    selectedExportOption,
    getOverallPerformanceData,
    getPerformanceByLensData,
}: {
    selectedExportOption: PerformanceSummaryExportOption;
    getOverallPerformanceData: () => ExportTableData | undefined;
    getPerformanceByLensData: () => ExportTableData | undefined;
}): ExportTableData => {
    if (selectedExportOption === 'overall_performance') {
        return getOverallPerformanceData() ?? EMPTY_EXPORT_TABLE_DATA;
    }
    if (selectedExportOption === 'performance_by_lens') {
        return getPerformanceByLensData() ?? EMPTY_EXPORT_TABLE_DATA;
    }
    return EMPTY_EXPORT_TABLE_DATA;
};

export const buildPerformanceSummaryKpiExportRows = (kpiData: ExportKpiItem[]): Array<Array<string | number>> => {
    const visibleKpis = kpiData.filter((kpi) => kpi.show !== false);
    const kpiRows: Array<Array<string | number>> = [[], ['KPI Name', 'Value', 'Growth %']];

    visibleKpis.forEach((kpi) => {
        kpiRows.push([
            kpi.display_label ?? kpi.title ?? kpi.kpi_id ?? '-',
            formatKpiExportValue(kpi),
            kpi.growth_percentage == null ? '-' : `${kpi.growth_percentage}%`,
        ]);
    });

    return kpiRows;
};

export const buildPerformanceSummarySectionExportRows = (tableData: ExportTableData): Array<Array<string | number>> => {
    const sectionRows: Array<Array<string | number>> = [];

    if (tableData.headers.length) sectionRows.push(tableData.headers);
    sectionRows.push(...tableData.rows);

    return sectionRows;
};

export type PpaSimulationPeriod = {
    start_timestamp?: number;
    end_timestamp?: number;
    simulation_period_type?: string;
};

export type PpaScopeItem = {
    dimension?: string;
    attribute_type?: string;
    attribute_ex_id?: string[];
};

export type PpaTableRowBase = {
    created_by?: string;
    status?: string;
    last_updated?: string;
    simulation_period?: PpaSimulationPeriod;
    scope?: PpaScopeItem[];
};

export const PPA_SCENARIO_STATUS_COLOR_MAPPING: Record<string, string> = {
    ready: 'success',
    in_progress: 'processing',
    failed: 'error',
    draft: 'default',
};

export const toPpaPublicRowRecord = <T extends { key: unknown; row_type: unknown }>(
    record: T,
): Omit<T, 'key' | 'row_type'> => {
    const { key: _key, row_type: _rowType, ...rest } = record;
    return rest as Omit<T, 'key' | 'row_type'>;
};

export const formatPpaSimulationPeriod = (period?: PpaSimulationPeriod) => {
    if (!period?.start_timestamp || !period?.end_timestamp) {
        return '-';
    }
    return `${dayjs.unix(period.start_timestamp).format('MM/DD/YY')} - ${dayjs
        .unix(period.end_timestamp)
        .format('MM/DD/YY')}`;
};

export const formatPpaCreator = (createdBy?: string) => {
    if (!createdBy) {
        return { initials: '--', value: '-' };
    }
    const localPart = createdBy.split('@')[0] || createdBy;
    const initials = localPart.slice(0, 2).toUpperCase();
    return { initials, value: createdBy };
};

export const formatPpaStatusLabel = (status?: string) => {
    if (!status) {
        return 'Draft';
    }
    if (status === 'ready') {
        return 'Active';
    }
    return status
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
};

export const getPpaColumnTextValue = (
    record: PpaTableRowBase & { project_name?: string },
    columnId?: string,
) => {
    if (!columnId) {
        return '-';
    }

    if (columnId === 'project_name') {
        return record.project_name || '-';
    }

    if (columnId === 'status') {
        return formatPpaStatusLabel(record.status);
    }

    if (columnId === 'created_by') {
        return record.created_by || '-';
    }

    if (columnId === 'last_updated') {
        return record.last_updated ? dayjs(record.last_updated).format('MMM D,YYYY') : '-';
    }

    if (columnId === 'simulation_period') {
        return formatPpaSimulationPeriod(record.simulation_period);
    }

    if (columnId === 'scope') {
        return record.scope?.length
            ? record.scope
                .map((scopeItem) => scopeItem.attribute_type || scopeItem.dimension || 'Scope')
                .join(', ')
            : '-';
    }

    const value = (record as Record<string, unknown>)[columnId];
    return typeof value === 'string' || typeof value === 'number' ? String(value) : '-';
};

type PpaAnalyticsEnablementMeta = {
    analytics_enabled?: boolean;
    features?: Array<{ feature_id?: string }>;
};

type PpaAnalyticsEnablementConfig = {
    enableAnalyticsTab?: boolean;
};

/** Whether scenario-manager analytics selection UI should be available (meta API or app config). */
export const isPpaAnalyticsEnabled = (
    scenarioManagerMeta?: PpaAnalyticsEnablementMeta,
    pricingSimulatorConfig?: PpaAnalyticsEnablementConfig,
): boolean => {
    if (typeof scenarioManagerMeta?.analytics_enabled === 'boolean') {
        return scenarioManagerMeta.analytics_enabled;
    }
    if (
        scenarioManagerMeta?.features?.some(
            (feature) => feature.feature_id === 'analytics' || feature.feature_id === 'simulation_analytics',
        )
    ) {
        return true;
    }
    return Boolean(pricingSimulatorConfig?.enableAnalyticsTab);
};

export const getPpaScenariosFromResponse = <T = unknown>(responseData: any): T[] => {
    if (Array.isArray(responseData?.scenarios)) {
        return responseData.scenarios;
    }
    if (Array.isArray(responseData?.scenario_list)) {
        return responseData.scenario_list;
    }
    return [];
};

export const getPpaScenarioCountFromResponse = (responseData: any, fallbackLength: number): number => {
    if (typeof responseData?.total_count === 'number') {
        return responseData.total_count;
    }
    if (typeof responseData?.scenario_count === 'number') {
        return responseData.scenario_count;
    }
    return fallbackLength;
};

/** Format view `updated_at` for the saved-views picker subtitle. */
export const formatPpaViewUpdatedAt = (updatedAt?: string): string => {
    if (!updatedAt) return '';
    const formatted = formatActivityDate(updatedAt);
    return formatted ?? '';
};

export const resolvePpaViewApiErrorMessage = (error: unknown, fallback: string): string =>
    resolvePpaCreateProjectApiErrorMessage(error, fallback);

export const buildLensConfigFromMetadata = (metadata: PPAGetViewMetadataResponse): PPALensConfigInput[] =>
    metadata.lenses.map((lens) => ({
        lens_id: lens.lens_id,
        is_selected: lens.is_selected,
        display_order: lens.display_order,
        is_default_lens: lens.is_default_lens,
    }));

export const buildGroupByFromMetadata = (metadata: PPAGetViewMetadataResponse) =>
    [...metadata.group_by]
        .sort((a, b) => a.display_order - b.display_order)
        .map((item) => ({
            attribute_key: item.attribute_key,
            display_order: item.display_order,
        }));

export const buildCreateViewPayloadFromMetadata = (
    metadata: PPAGetViewMetadataResponse,
    options: {
        viewName: string;
        filters: PPAScopeFilter[];
        isDefault: boolean;
        scenarioId?: number | null;
    },
): PPACreateViewRequest => ({
    view_name: options.viewName,
    filters: options.filters,
    group_by: buildGroupByFromMetadata(metadata),
    lens_config: buildLensConfigFromMetadata(metadata),
    column_visibility: metadata.column_visibility?.map((item) => ({
        lens_id: item.lens_id,
        attribute_key: item.attribute_key,
        item_type: item.item_type,
        is_display_enabled: item.is_display_enabled,
    })),
    active_lens_id: metadata.active_lens_id ?? null,
    is_default: options.isDefault,
    scenario_id: options.scenarioId ?? null,
});

export const buildCreateViewPayloadFromLiveConfig = (
    live: {
        lens_config: PPALensConfigInput[];
        group_by: PPAGroupByInput[];
        active_lens_id: number | null;
        column_visibility: PPAScenarioColumnVisibilityInput[];
    },
    options: {
        viewName: string;
        filters: PPAScopeFilter[];
        isDefault: boolean;
        scenarioId?: number | null;
    },
): PPACreateViewRequest => ({
    view_name: options.viewName,
    filters: options.filters,
    group_by: live.group_by,
    lens_config: live.lens_config,
    column_visibility: live.column_visibility,
    active_lens_id: live.active_lens_id,
    is_default: options.isDefault,
    scenario_id: options.scenarioId ?? null,
});

export const buildUpdateViewPayloadFromMetadata = (
    metadata: PPAGetViewMetadataResponse,
    updatedAt: string | null,
    overrides: {
        is_default?: boolean;
        view_name?: string;
        filters?: PPAScopeFilter[];
        metadataOnly?: boolean;
    } = {},
): PPACreateViewRequest => {
    const metadataOnly = overrides.metadataOnly ?? false;

    return {
        view_id: metadata.view_id,
        updated_at: updatedAt,
        view_name: overrides.view_name ?? metadata.view_name,
        filters: overrides.filters ?? metadata.filters,
        is_default: overrides.is_default ?? metadata.is_default,
        ...(metadataOnly
            ? {}
            : {
                  group_by: buildGroupByFromMetadata(metadata),
                  lens_config: buildLensConfigFromMetadata(metadata),
              }),
    };
};